import {
  useProjectOurWorkItemsQuery,
  useTimeTrackingProjectReportQuery,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { withCan } from "@src/utils/components/permissions";
import { useUpdateURI } from "@src/utils/hooks";
import { getQueryParam, getQueryParamOptional } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { CardReport } from "./CardReport";
import { Store } from "./Store";

export const PageProjectTimeReportCtx = React.createContext<Store>({} as Store);

function useData(store: Store, id: string) {
  const projectReq = useProjectOurWorkItemsQuery({
    variables: { id },
    skip: !id,
    onCompleted(data) {
      store.fromProjectOurWorkItemsQuery(data);
    },
    ...onError(),
  });

  const reportReq = useTimeTrackingProjectReportQuery({
    skip: !id || !store.activeOrderByColumn,
    variables: {
      filters: {
        project_id: id,
        orderBy: store.orderBy.asWhereParam,
        where: store.asWhereParam,
      },
    },
    onCompleted(data) {
      store.fromTimeTrackingProjectReportQuery(data);
    },
    ...onError(),
  });

  return { projectReq, reportReq };
}

export const PageProjectTimeReport = withCan(
  {
    do: [
      "project_timeTrackingReport_read_all",
      "project_timeTrackingReport_read_own",
      "project_timeTrackingReport_read_team",
    ],
  },
  observer(function PageProjectTimeReport() {
    const { query } = useRouter();
    const id = getQueryParam("id", query);
    const budgetItemId = getQueryParamOptional("budgetItemId", query);

    const [store] = useState(() => {
      const store = Store.create();
      store.projectId = id;
      store.orderBy.fromURLSearchParam(query);

      if (store.orderBy.clauses.length === 0) {
        store.fetchMonthlyBreakdown();
      }

      budgetItemId && store.form.fields.budgetItem.onChange(budgetItemId);
      return store;
    });

    useUpdateURI(
      {
        orderBy: store.orderBy.asURLSearchParam.orderBy,
      },
      "id",
    );

    const { projectReq, reportReq } = useData(store, id);
    const isLoading = projectReq.loading || reportReq.loading;

    return (
      <PageProjectTimeReportCtx.Provider value={store}>
        <CardReport isLoading={isLoading} />
      </PageProjectTimeReportCtx.Provider>
    );
  }),
);
